import Icon from "@components/Icon";
import {
  APPROVALS,
  APPROVAL_WORKFLOW,
  CATEGORY_LIST,
  CHALLAN,
  DAMAGE,
  DASHBOARD,
  DIRECT_PURCHASE,
  GATE_PASS,
  INTERNAL_REQUISITION,
  INVENTORY,
  ITEM_CODE_SETTINGS,
  MASTER_DATA_LIST,
  MRR,
  MY_REQUESTS,
  PENDING_APPROVALS,
  PROCUREMENT,
  PRODUCT_LIST,
  PURCHASE_ORDER,
  QUALITY_CERTIFICATE,
  REPORTS,
  REPORT_GRN,
  REPORT_INVENTORY,
  REPORT_PRODUCT_LEDGER,
  REPORT_PURCHASES,
  REPORT_REQ_TRACKING,
  REPORT_SPR_DETAIL,
  REPORT_STOCK_LEDGER,
  REQUISITION_ISSUE,
  RPT_PURCHASES,
  SETUP,
  SPR,
  STOCK,
  STOCK_ADJUSTMENT,
  STOCK_TRANSFER,
  SUB_CATEGORY_LIST,
  SUPPLIER,
  UOM_LIST,
  VENDOR_LIST,
  WAREHOUSE_LIST,
} from "@constants/internal-route.constant";
import { ROUTE_CODES } from "@constants/route-codes.constant";
import { ReactNode } from "react";
import { FaBalanceScale } from "react-icons/fa";

/**
 * The stock navigation, rendered by the left sidebar (sidebar/SidebarMenuMain). Moved here unchanged
 * from the old header MenuInner (the groups) and aside AsideMenuMain (the top items) when the menu
 * moved into the sidebar.
 *
 * stock gates by `feature.code === route_code`, not by url (see canSee). Titles are react-intl ids.
 */
export interface IMenuItemConfig {
  to: string;
  title: string;
  icon?: string;
  cust_icon?: ReactNode;
  route_code?: string;
  /** Draw a thin rule above this item, to split a group into its stages. */
  has_separator?: boolean;
}

export interface IMenuGroupConfig extends IMenuItemConfig {
  children: IMenuItemConfig[];
  /** Start this group open in the sidebar. Every other group starts folded. */
  open_by_default?: boolean;
}

/**
 * Dashboard, above the groups. The POS till and Shift items were removed on 2026-10-03: the till
 * is the deferred Sales product, stock has no route for either, and they had stayed hidden only
 * because their code (POS) has no feature row. COMPANY_ADMIN now bypasses gating, so a retired
 * screen has to be removed, not hidden behind a missing code.
 */
export const topItems: IMenuItemConfig[] = [
  {
    to: DASHBOARD,
    title: "MENU.DASHBOARD",
    icon: "bi-columns-gap",
    route_code: ROUTE_CODES.DASHBOARD,
  },
];

export const menus: IMenuGroupConfig[] = [
  {
    to: SETUP,
    title: "MENU.SETUP",
    icon: "bi-gear",
    route_code: ROUTE_CODES.POS_SETUP,
    children: [
      {
        to: MASTER_DATA_LIST,
        title: "MENU.MASTER_DATA",
        icon: "bi-collection",
        route_code: ROUTE_CODES.SETUP_MASTER_DATA,
      },
      {
        to: UOM_LIST,
        title: "MENU.UNIT_OF_MEASUREMENT",
        icon: "bi-box",
        route_code: ROUTE_CODES.SETUP_UOM,
      },
      {
        to: CATEGORY_LIST,
        title: "MENU.CATEGORY",
        icon: "bi-diagram-3",
        route_code: ROUTE_CODES.SETUP_CATEGORY,
      },
      {
        to: SUB_CATEGORY_LIST,
        title: "MENU.SUB_CATEGORY",
        icon: "bi-diagram-3-fill",
        route_code: ROUTE_CODES.SETUP_SUB_CATEGORY,
      },
      {
        to: PRODUCT_LIST,
        title: "MENU.PRODUCTS",
        icon: "bi-archive",
        route_code: ROUTE_CODES.SETUP_PRODUCT,
      },
      {
        to: ITEM_CODE_SETTINGS,
        title: "MENU.ITEM_CODE_SETTINGS",
        icon: "bi-upc",
        route_code: ROUTE_CODES.SETUP_ITEM_CODE_CONFIG,
      },
      {
        to: WAREHOUSE_LIST,
        title: "MENU.WAREHOUSE",
        icon: "bi-shop",
        route_code: ROUTE_CODES.SETUP_WAREHOUSE,
        has_separator: true,
      },
      {
        to: SUPPLIER,
        title: "MENU.SUPPLIERS",
        icon: "bi-people",
        route_code: ROUTE_CODES.SETUP_SUPPLIER,
      },
      {
        to: VENDOR_LIST,
        title: "MENU.VENDOR",
        icon: "bi-people-fill",
        route_code: ROUTE_CODES.SETUP_VENDOR,
      },
      {
        to: APPROVAL_WORKFLOW,
        title: "MENU.APPROVAL_WORKFLOW",
        icon: "bi-diagram-3",
        route_code: ROUTE_CODES.SETUP_APPROVAL_WORKFLOW,
      },
    ],
  },
  {
    to: INVENTORY,
    title: "MENU.INVENTORY",
    icon: "bi-basket",
    route_code: ROUTE_CODES.INVENTORY,
    open_by_default: true,
    children: [
      {
        to: INTERNAL_REQUISITION,
        title: "MENU.INTERNAL_REQUISITION",
        icon: "bi-cart-plus",
        route_code: ROUTE_CODES.INV_INTERNAL_REQUISITION,
      },
      {
        // Every child here is individually gated. renderSubMenu treats a MISSING route_code as
        // "always render", so leaving one off silently makes that screen ungrantable -- which is
        // what made Inventory all-or-nothing. A code added here must have a matching
        // auth.features row (sql/stock_acl_granularity.sql) or the screen hides from everybody.
        to: REQUISITION_ISSUE,
        title: "MENU.REQUISITION_ISSUE",
        icon: "bi-box-arrow-right",
        route_code: ROUTE_CODES.INV_REQUISITION_ISSUE,
      },
      {
        to: STOCK_TRANSFER,
        title: "MENU.TRANSFER_CONSUMPTION",
        cust_icon: <Icon icon="move_up" />,
        route_code: ROUTE_CODES.INV_TRANSFER,
      },
      // The procurement chain the STORE works, in the order the documents are raised:
      // requisition -> SPR -> PO -> challan -> QC -> MRR. The PO is the buyer's document and lives
      // in Procurement; these four are the store's, and sit between the requisition they answer and
      // the stock they land in. Their route_codes are unchanged, so no grant moved with them.
      {
        to: SPR,
        title: "MENU.SPR",
        icon: "bi-basket",
        route_code: ROUTE_CODES.PROCUREMENT_SPR,
        has_separator: true,
      },
      {
        to: CHALLAN,
        title: "MENU.CHALLAN",
        icon: "bi-truck",
        route_code: ROUTE_CODES.PROCUREMENT_CHALLAN,
      },
      {
        to: QUALITY_CERTIFICATE,
        title: "MENU.QUALITY_CERTIFICATE",
        icon: "bi-clipboard-check",
        route_code: ROUTE_CODES.PROCUREMENT_QC,
      },
      {
        to: MRR,
        title: "MENU.MRR",
        icon: "bi-box-seam",
        route_code: ROUTE_CODES.PROCUREMENT_MRR,
      },
      {
        to: STOCK,
        title: "MENU.STOCKS",
        cust_icon: <FaBalanceScale />,
        route_code: ROUTE_CODES.INV_STOCKS,
        has_separator: true,
      },
      {
        to: STOCK_ADJUSTMENT,
        title: "MENU.STOCK_ADJUSTMENT",
        icon: "bi-sliders",
        route_code: ROUTE_CODES.INV_STOCK_ADJUSTMENT,
      },
      {
        to: DAMAGE,
        title: "MENU.DAMAGE",
        icon: "bi-exclamation-triangle",
        route_code: ROUTE_CODES.INV_DAMAGE,
      },
      {
        to: GATE_PASS,
        title: "MENU.GATE_PASS",
        icon: "bi-box-arrow-right",
        route_code: ROUTE_CODES.INV_GATE_PASS,
      },
    ],
  },
  {
    to: PROCUREMENT,
    title: "MENU.PROCUREMENT",
    icon: "bi-bag-plus",
    route_code: ROUTE_CODES.PROCUREMENT,
    // Procurement is the BUYING menu: the two documents that commit money to a supplier. An
    // order is raised against an approved SPR; a direct purchase is the door for buying without
    // one, on its own table. Everything the store then does with the goods is under Inventory.
    children: [
      {
        to: PURCHASE_ORDER,
        title: "MENU.PURCHASE_ORDER",
        icon: "bi-cart-check",
        route_code: ROUTE_CODES.PROCUREMENT_PO,
      },
      {
        to: DIRECT_PURCHASE,
        title: "MENU.DIRECT_PURCHASE",
        icon: "bi-cart-plus",
        route_code: ROUTE_CODES.INV_RECEIVE,
      },
    ],
  },
  {
    to: APPROVALS,
    title: "MENU.APPROVALS",
    icon: "bi-check2-square",
    route_code: ROUTE_CODES.APPROVALS,
    children: [
      {
        to: MY_REQUESTS,
        title: "MENU.MY_REQUESTS",
        icon: "bi-send",
        route_code: ROUTE_CODES.MY_REQUESTS,
      },
      {
        to: PENDING_APPROVALS,
        title: "MENU.PENDING_APPROVALS",
        icon: "bi-inbox",
        route_code: ROUTE_CODES.PENDING_APPROVALS,
      },
    ],
  },
  {
    to: REPORTS,
    title: "MENU.REPORTS",
    icon: "bi-graph-up-arrow",
    route_code: ROUTE_CODES.REPORTS,
    children: [
      {
        to: REPORT_INVENTORY,
        title: "MENU.RPT_INVENTORY",
        icon: "bi-boxes",
        route_code: ROUTE_CODES.RPT_INVENTORY,
      },
      {
        to: REPORT_PURCHASES,
        title: "MENU.RPT_PURCHASES",
        icon: "bi-bag-check",
        route_code: ROUTE_CODES.RPT_PURCHASES,
      },
      {
        to: REPORT_STOCK_LEDGER,
        title: "MENU.RPT_STOCK_LEDGER",
        icon: "bi-journal-text",
        route_code: ROUTE_CODES.RPT_STOCK_LEDGER,
      },
      {
        to: REPORT_PRODUCT_LEDGER,
        title: "MENU.RPT_PRODUCT_LEDGER",
        icon: "bi-card-list",
        route_code: ROUTE_CODES.RPT_PRODUCT_LEDGER,
      },
      {
        to: REPORT_GRN,
        title: "MENU.RPT_GRN",
        icon: "bi-clipboard-check",
        route_code: ROUTE_CODES.RPT_GRN,
      },
      {
        to: REPORT_REQ_TRACKING,
        title: "MENU.RPT_REQ_TRACKING",
        icon: "bi-diagram-3",
        route_code: ROUTE_CODES.RPT_REQ_TRACKING,
      },
      {
        to: REPORT_SPR_DETAIL,
        title: "MENU.RPT_SPR_DETAIL",
        icon: "bi-file-earmark-text",
        route_code: ROUTE_CODES.RPT_SPR_DETAIL,
      },
    ],
  },
];

// Visibility is usePermissions().canSee (@services/helper/permission.helper): a MISSING route_code
// means "always visible" -- which is how Inventory ended up ungated. Keep that fallback (some
// children are deliberately open), but every gated one must name a real auth.features row.
